<template>
  <div
    :class="[
      'bg-[--color-body-bg]',
      'p-6',
      'w-full',
      'aspect-video',
      'overflow-hidden',
      'rounded-lg',
      'flex',
      'flex-row',
      'justify-between',
      'gap-6',
    ]"
  >
    <!-- Text -->
    <div
      class="flex flex-col w-full"
      :class="{ 'order-2': sliderItem.imagePosition === 'left', 'order-1': sliderItem.imagePosition !== 'left' }"
    >
      <div class="flex-block text-lg font-semibold truncate h-7 w-full mb-2">
        {{ sliderItem.title }}
      </div>

      <div v-if="linkList?.length" class="flex flex-col">
        <InnoContentSliderItemLink
          v-for="(link, index) in linkList"
          :key="index"
          :linkLabel="link.label!"
          :linkUrl="link.link!"
        />
      </div>

      <div
        v-else
        ref="textContainer"
        class="dynamic-ellipsis text-base"
        :class="{ 'h-full': !hasTextLink }"
        v-html-safe="formattedText"
      ></div>

      <div v-if="hasTextLink" class="flex h-6 mt-2 items-end">
        <!-- Innovadis custom: sanitize slider external URL before binding to href -->
        <a
          v-if="isExternalLink"
          target="_blank"
          class="text-[--color-link] text-sm font-semibold"
          rel="noopener noreferrer"
          :href="getSafeUrl(sliderItem.linkUrl as string)"
        >
          {{ sliderItem.linkLabel }}
        </a>
        <router-link v-else :to="sliderItem.linkUrl || '#'">
          <span class="text-[--color-link] text-base font-semibold">{{ sliderItem.linkLabel }}</span>
        </router-link>
      </div>
    </div>

    <!-- Image -->
    <div
      v-if="sliderItem.image"
      class="bg-center bg-no-repeat w-[100%] rounded-lg"
      :class="{
        'bg-cover': sliderItem.backgroundCover,
        'order-1': sliderItem.imagePosition === 'left',
        'order-2': sliderItem.imagePosition !== 'left',
      }"
      :style="{ backgroundImage: `url(${sliderItem.image})` }"
      :title="sliderItem.imageDescription"
    ></div>
  </div>
</template>

<script setup lang="ts">
import DOMPurify from "dompurify";
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import type { IContentSliderType } from "@/core/types/innovadis/inno-content-slider-item";
import InnoContentSliderItemLink from "./inno-content-slider-item-link.vue";
import { getLinkAttr } from "@/core/utilities/common";
import { getSafeUrl } from "@/core/utilities/url/safeUrl";

interface IProps {
  sliderItem: IContentSliderType;
}

const props = defineProps<IProps>();

const hasTextLink = computed(() => {
  const { linkLabel, linkUrl } = props.sliderItem;
  return !!linkLabel?.trim() && !!linkUrl?.trim();
});

const isExternalLink = computed(() => {
  return "externalLink" in getLinkAttr(props.sliderItem.linkUrl);
});

const linkList = computed(() =>
  props.sliderItem.links?.filter(
    (item) =>
      typeof item.label === "string" &&
      item.label.trim().length > 0 &&
      typeof item.link === "string" &&
      item.link.trim().length > 0,
  ),
);

const textContainer = ref<HTMLElement | null>(null);

const formattedText = computed(() =>
  DOMPurify.sanitize(props.sliderItem.text?.replace(/(?:\r\n|\r|\n)/g, "<br />") as string, {
    USE_PROFILES: { html: true },
  }),
);

const applyDynamicClamp = () => {
  nextTick(() => {
    const el = textContainer.value;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight);
    const containerHeight = el.clientHeight;

    const lines = Math.floor(containerHeight / lineHeight);

    // Apply clamp
    el.style.webkitLineClamp = lines.toString();
  });
};

onMounted(() => {
  applyDynamicClamp();
  window.addEventListener("resize", applyDynamicClamp);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", applyDynamicClamp);
});
</script>
<style type="scss">
.dynamic-ellipsis {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
